/**
 * PageCanvas — grille de mise en page du builder de rapports Orion (Phase 2a).
 *
 * Porté depuis Metrix (`frontend/src/features/reports/PageCanvas.tsx` +
 * `gridConfig.ts`), réduit au strict nécessaire pour cette phase : pas de
 * multi-sélection, pas de toolbar par widget (dupliquer/verrouiller/style),
 * pas de fallback responsive mobile — uniquement le canevas d'édition/preview
 * desktop avec drag/resize/drop sur une grille de 120 colonnes.
 *
 * react-grid-layout v2.2.3 utilise une API par groupes de config
 * (`gridConfig`, `dragConfig`, `resizeConfig`, `dropConfig`) plutôt que des
 * props plates — voir `GridLayoutProps` dans le package.
 */
import GridLayout, { type Layout as RglLayout, type LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, LayoutGrid, Lock, LockOpen, Paintbrush, Pencil, Pipette, Trash2 } from 'lucide-react';
import { useWidgetData } from './useWidgetData';
import WidgetRenderer from './WidgetRenderer';
import { widgetDefaults, defaultWidgetConfig } from './widgetDefaults';
import { copyStyle, getClipboard, pasteStyle, subscribeClipboard } from './styleClipboard';
import { computeGroupMove, type GroupDragState } from './groupMove';
import { resolveChartPalette } from './chartPalette';
import { ChartPaletteProvider } from './ChartPaletteContext';
import type { Layout, Widget, WidgetType, PeriodValue } from './schemas';
import type { ReportTheme } from '../../types/reportTheme';
import type { MapView } from './widgets/mapLayers';

/** Nombre de colonnes de la grille du builder (identique à Metrix). */
const GRID_COLS = 120;
/** Hauteur d'une rangée en pixels. */
const GRID_ROW_HEIGHT = 20;
/** Marge horizontale/verticale entre widgets en pixels. */
const GRID_MARGIN: [number, number] = [10, 10];
/** Padding interne du conteneur de grille en pixels. */
const GRID_CONTAINER_PADDING: [number, number] = [16, 16];
/** Largeur fixe du canevas en pixels (desktop uniquement pour cette phase). */
const GRID_CANVAS_WIDTH = 1100;
/** Hauteur fixe du canevas (identique Metrix). CRITIQUE : sans hauteur fixe, une
 *  grille vide fait 0px de haut → aucune zone de drop → onDrop ne se déclenche jamais. */
const GRID_CANVAS_HEIGHT = 1556;
/** Nombre max de rangées (identique Metrix). */
const GRID_MAX_ROWS = 69;

export interface PageCanvasProps {
  layout: Layout;
  mode: 'edit' | 'preview';
  period: PeriodValue;
  selectedWidgetId?: string | null;
  /** Type du widget en cours de drag depuis la palette (null si aucun). */
  draggingType?: WidgetType | null;
  /** Thème actif du rapport (palette de séries, branding) — optionnel. */
  theme?: ReportTheme | null;
  onLayoutChange: (layout: Layout) => void;
  onSelectWidget: (id: string | null) => void;
  /** Ouvre le drawer de config sur ce widget (déclenché par le crayon uniquement) ;
   *  `null` le ferme. Découplé de la sélection : sélectionner un widget ne l'ouvre pas. */
  onEditWidget: (id: string | null) => void;
  /** Contenu d'un bloc `rich_text` édité inline dans le canvas (TipTap). */
  onWidgetContentChange?: (widgetId: string, html: string) => void;
  /** Vue figée capturée sur un widget `map` (bouton « Figer »/« Réinitialiser »). */
  onWidgetViewChange?: (widgetId: string, view: MapView | null) => void;
}

/**
 * Sélecteurs exclus du drag de la grille (`dragConfig.cancel`). Sans eux, le
 * `mousedown` dans l'éditeur de texte riche démarre un drag du widget : impossible
 * de poser le curseur ou de sélectionner du texte.
 * NB : dans ce fork (react-grid-layout v2), l'option s'appelle `cancel`, pas
 * `draggableCancel` (voir `DragConfig` dans les types du package).
 */
// `.leaflet-container` : sans lui, un pan/zoom sur une carte en mode édition démarre
// un drag de la grille (le widget se déplace) au lieu de recadrer la carte — ce qui
// rendait le bouton « Figer la vue » inopérant (impossible de cadrer avant de figer).
// Conséquence assumée : on déplace un widget carte par sa barre du haut / son padding.
const DRAG_CANCEL_SELECTOR = '.rich-text-editable, .ProseMirror, .leaflet-container';

function genWidgetId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `w_${crypto.randomUUID()}`;
  return `w_${Math.random().toString(36).slice(2)}`;
}

export default function PageCanvas({
  layout,
  mode,
  period,
  selectedWidgetId,
  draggingType,
  theme,
  onLayoutChange,
  onSelectWidget,
  onEditWidget,
  onWidgetContentChange,
  onWidgetViewChange,
}: PageCanvasProps) {
  const widgets = layout.widgets;
  const isEdit = mode === 'edit';
  const chartPalette = resolveChartPalette(theme?.chartPalette);

  // Ref toujours à jour vers les widgets courants. Indispensable : après un drop,
  // react-grid-layout refire onLayoutChange dans la foulée ; sans ref, la closure
  // capturerait la liste d'AVANT le drop et écraserait le widget qu'on vient d'ajouter.
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;

  const { dataByWidgetId, loading, errorByWidgetId } = useWidgetData(widgets, period);

  // Type du style actuellement en clipboard (null si rien copié) — pilote l'affichage
  // du bouton « Coller le style » ; réabonnement pour se rafraîchir après un copyStyle().
  const [clipType, setClipType] = useState<string | null>(() => getClipboard()?.type ?? null);
  useEffect(() => subscribeClipboard(() => setClipType(getClipboard()?.type ?? null)), []);

  // Multi-sélection (shift-clic) : ids sélectionnés + ref toujours à jour pour les closures
  // (handleLayoutChange/handleDeleteFromCanvas lisent la sélection courante sans la capturer périmée).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  // Positions connues au frame précédent + état du drag groupé (driver/followers) — portés par
  // `computeGroupMove`, réinitialisés à chaque nouvelle interaction de sélection.
  const prevPositions = useRef(new Map<string, { x: number; y: number }>());
  const groupDragRef = useRef<GroupDragState | null>(null);

  const rglLayout: RglLayout = useMemo(
    () =>
      widgets.map((w) => ({
        i: w.id,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        minW: 1,
        minH: 1,
        maxW: GRID_COLS,
        isDraggable: w.locked ? false : undefined,
        isResizable: w.locked ? false : undefined,
      })),
    [widgets],
  );

  function handleLayoutChange(next: RglLayout) {
    // Lire la liste COURANTE via la ref (jamais la closure périmée) — sinon le
    // onLayoutChange que RGL émet juste après un drop réécrit l'ancienne liste.
    const ws = widgetsRef.current;
    let updated: Widget[];
    if (selectedIdsRef.current.size > 1) {
      // Plusieurs widgets sélectionnés : déplacement groupé (driver/followers).
      const result = computeGroupMove(ws, [...next], selectedIdsRef.current, groupDragRef.current, prevPositions.current);
      updated = result.updated;
      groupDragRef.current = result.state;
    } else {
      updated = ws.map((w) => {
        const found = next.find((item) => item.i === w.id);
        return found ? { ...w, x: found.x, y: found.y, w: found.w, h: found.h } : w;
      });
    }

    const same = updated.every((u, i) =>
      ws[i] && u.id === ws[i].id && u.x === ws[i].x && u.y === ws[i].y &&
      u.w === ws[i].w && u.h === ws[i].h
    );
    if (!same) onLayoutChange({ widgets: updated });
  }

  function handleDrop(_next: RglLayout, item: LayoutItem | undefined) {
    if (!draggingType || !item) return;
    const def = widgetDefaults[draggingType];
    const newWidget: Widget = {
      id: genWidgetId(),
      type: draggingType,
      x: Math.max(0, Math.min(GRID_COLS - def.w, item.x)),
      y: item.y,
      w: def.w,
      h: def.h,
      config: defaultWidgetConfig(draggingType),
    };
    onLayoutChange({ widgets: [...widgetsRef.current, newWidget] });
    onSelectWidget(newWidget.id);
  }

  function handleDuplicate(id: string) {
    const w = widgetsRef.current.find((x) => x.id === id);
    if (!w) return;
    onLayoutChange({ widgets: [...widgetsRef.current, { ...w, id: genWidgetId(), y: w.y + w.h }] });
  }

  function handleToggleLock(id: string) {
    onLayoutChange({ widgets: widgetsRef.current.map((w) => (w.id === id ? { ...w, locked: !w.locked } : w)) });
  }

  function handleDeleteFromCanvas(id: string) {
    // Si le widget cliqué fait partie d'une sélection multiple, on supprime tout le groupe.
    const sel = selectedIdsRef.current;
    const ids = sel.size > 1 && sel.has(id) ? sel : new Set([id]);
    if (!window.confirm(ids.size > 1 ? `Supprimer ${ids.size} widgets ?` : 'Supprimer ce widget ?')) return;
    onLayoutChange({ widgets: widgetsRef.current.filter((w) => !ids.has(w.id)) });
    if (selectedWidgetId && ids.has(selectedWidgetId)) onSelectWidget(null);
    onEditWidget(null); // un widget supprimé ne doit pas laisser son drawer ouvert
    setSelectedIds(new Set());
  }

  return (
    <div className="flex-1 overflow-auto bg-[#F4F5FA] p-8">
      <div
        className="relative mx-auto rounded-lg bg-white shadow-md"
        style={{ width: GRID_CANVAS_WIDTH, minHeight: 400 }}
        onClick={(e) => {
          // Clic « dans le vide » (fond du canvas) : désélectionne et ferme le drawer.
          if (e.target === e.currentTarget) {
            onSelectWidget(null);
            onEditWidget(null);
          }
        }}
      >
        <ChartPaletteProvider palette={chartPalette}>
        <GridLayout
          layout={rglLayout}
          width={GRID_CANVAS_WIDTH}
          autoSize={false}
          style={{ height: GRID_CANVAS_HEIGHT }}
          gridConfig={{
            cols: GRID_COLS,
            rowHeight: GRID_ROW_HEIGHT,
            margin: GRID_MARGIN,
            containerPadding: GRID_CONTAINER_PADDING,
            maxRows: GRID_MAX_ROWS,
          }}
          dragConfig={{ enabled: isEdit, bounded: true, cancel: DRAG_CANCEL_SELECTOR }}
          resizeConfig={{
            enabled: isEdit,
            handles: ['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw'],
          }}
          dropConfig={{
            enabled: isEdit,
            defaultItem: {
              w: draggingType ? widgetDefaults[draggingType].w : 3,
              h: draggingType ? widgetDefaults[draggingType].h : 2,
            },
          }}
          onLayoutChange={handleLayoutChange}
          onDrop={handleDrop}
        >
          {widgets.map((w) => (
            <div
              key={w.id}
              className="group relative h-full w-full"
              onClickCapture={(e) => {
                if (!isEdit) return;
                // `[data-toolbar]` : barre d'actions du widget (crayon, cadenas…).
                // `[data-widget-interactive]` : contrôles internes d'un widget qui doivent
                // rester cliquables sans déclencher la sélection (ex. chevron d'expansion
                // du tableau) — sans cette exemption, `stopPropagation` ci-dessous avale le
                // clic en phase capture avant qu'il n'atteigne le handler React du contrôle.
                if ((e.target as Element).closest('[data-toolbar], [data-widget-interactive]')) return;
                e.stopPropagation();
                // Nouvelle interaction de sélection : le drag groupé en cours (s'il y en a un) n'a
                // plus de sens, on repart de zéro au prochain déplacement.
                groupDragRef.current = null;
                // Sélectionner un widget (corps, pas crayon) ferme le drawer : il ne se
                // rouvre que via le crayon. Les clics sur `[data-toolbar]`/`[data-widget-interactive]`
                // sont exclus plus haut.
                onEditWidget(null);
                if (e.shiftKey) {
                  setSelectedIds((prev) => {
                    const n = new Set(prev);
                    if (n.has(w.id)) n.delete(w.id);
                    else n.add(w.id);
                    return n;
                  });
                } else {
                  const willSelect = w.id !== selectedWidgetId;
                  setSelectedIds(willSelect ? new Set([w.id]) : new Set());
                  onSelectWidget(willSelect ? w.id : null);
                }
              }}
            >
              {isEdit && selectedIds.has(w.id) && (
                <div data-testid="sel-ring" className="pointer-events-none absolute inset-0 z-30 rounded-lg ring-2 ring-[#6366F1] ring-offset-1 ring-offset-white" />
              )}
              <WidgetRenderer
                widget={w}
                data={dataByWidgetId[w.id] ?? null}
                loading={loading}
                error={errorByWidgetId[w.id]}
                // Les blocs de mise en page (période affichée) n'interrogent rien :
                // sans la période du rapport, ils n'auraient rien à afficher.
                period={{ start: period.start, end: period.end }}
                // Édition inline : `rich_text` (TipTap) et `map` (contrôle Figer/Réinitialiser),
                // et seulement en mode édition.
                editable={isEdit && (w.type === 'rich_text' || w.type === 'map')}
                onContentChange={(html) => onWidgetContentChange?.(w.id, html)}
                onViewChange={(view) => onWidgetViewChange?.(w.id, view)}
              />
              {isEdit && (
                <div
                  data-toolbar
                  // z-[1100] : au-dessus des panes/contrôles Leaflet (tuiles 200, popups 700,
                  // contrôles 1000) — sinon la barre d'actions (Figer, Verrouiller…) passe SOUS
                  // la carte sur un widget `map` et devient inutilisable.
                  className="absolute right-1 top-1 z-[1100] hidden items-center gap-0.5 rounded-md border border-[#E2E4ED] bg-white/95 p-0.5 shadow-sm group-hover:flex"
                >
                  <button
                    type="button"
                    title="Configurer"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Le crayon est le SEUL point d'ouverture du drawer : sélectionne + ouvre.
                      onSelectWidget(w.id);
                      onEditWidget(w.id);
                    }}
                    className="rounded p-1 text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#6366F1]"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    title={w.locked ? 'Déverrouiller' : 'Verrouiller (désactive déplacement/redimensionnement)'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLock(w.id);
                    }}
                    className={
                      w.locked
                        ? 'rounded bg-amber-100 p-1 text-amber-600'
                        : 'rounded p-1 text-[#8B8FA7] hover:bg-amber-50 hover:text-amber-600'
                    }
                  >
                    {w.locked ? <Lock size={13} /> : <LockOpen size={13} />}
                  </button>
                  <button
                    type="button"
                    title="Copier le style"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyStyle(w.type, w.config as Record<string, unknown>);
                    }}
                    className="rounded p-1 text-[#8B8FA7] hover:bg-violet-50 hover:text-violet-600"
                  >
                    <Pipette size={13} />
                  </button>
                  {clipType !== null && (
                    <button
                      type="button"
                      title="Coller le style"
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = pasteStyle(w.config as Record<string, unknown>);
                        if (!next) return;
                        onLayoutChange({
                          widgets: widgetsRef.current.map((x) => (x.id === w.id ? { ...x, config: next } : x)),
                        });
                      }}
                      className="rounded p-1 text-violet-600 hover:bg-violet-50"
                    >
                      <Paintbrush size={13} />
                    </button>
                  )}
                  <button
                    type="button"
                    title="Dupliquer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicate(w.id);
                    }}
                    className="rounded p-1 text-[#8B8FA7] hover:bg-emerald-50 hover:text-emerald-600"
                  >
                    <Copy size={13} />
                  </button>
                  <button
                    type="button"
                    title="Supprimer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFromCanvas(w.id);
                    }}
                    className="rounded p-1 text-[#8B8FA7] hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </GridLayout>
        </ChartPaletteProvider>
        {widgets.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#6366F1]/[0.06] text-[#6366F1]">
              <LayoutGrid className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1B2559]">Page vide</p>
              <p className="mt-0.5 text-xs text-[#8B8FA7]">
                {isEdit
                  ? 'Glisse un widget depuis la palette de gauche pour commencer.'
                  : 'Aucun widget sur cette page.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
